import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

type Size = {
  width: number | undefined;
  height: number | undefined;
};
type ElementType = HTMLDivElement | HTMLButtonElement | null;
type ReturnType = Size & {
  setRef: (node: ElementType) => void;
};
export function useSizeObserver(defaultSize?: Size): ReturnType {
  const ref = useRef<ElementType>(null);
  // NOTE: we use the fact that setWidth(width) does nothing
  const [width, setWidth] = useState(defaultSize?.width);
  const [height, setHeight] = useState(defaultSize?.width);
  const resizeObserver = useMemo(
    () =>
      new ResizeObserver(() => {
        const boundingRect = ref.current?.getBoundingClientRect();
        setWidth(boundingRect?.width ?? defaultSize?.width);
        setHeight(boundingRect?.height ?? defaultSize?.height);
      }),
    [defaultSize?.width, defaultSize?.height]
  );
  const setRef = useCallback(
    (node: ElementType) => {
      if (ref.current) {
        resizeObserver.unobserve(ref.current);
      }
      ref.current = node;
      if (ref.current) {
        resizeObserver.observe(ref.current);
        const boundingRect = ref.current?.getBoundingClientRect();
        setWidth(boundingRect?.width ?? defaultSize?.width);
        setHeight(boundingRect?.height ?? defaultSize?.height);
      }
    },
    [resizeObserver, ref, defaultSize?.width, defaultSize?.height]
  );
  useEffect(() => {
    // NOTE: remove the observer as soon as the component is unmounted
    return () => {
      resizeObserver.disconnect();
    };
  }, [resizeObserver, ref]);

  return {setRef, width, height};
}
